import type { DashboardComponent, DashboardFilterOption } from '../services/dashboard.service';

/**
 * What a save would change about a dashboard's saved defaults.
 *
 * Pulled out of DashboardDetail so it can be tested directly. This is the logic
 * behind the "update the defaults?" dialog — the only way a default ever gets
 * set — and it was wrong in a way no source-reading test could see: it walked
 * the FILTER components and not the dashboard's own `filters`, so a filter-bar
 * filter could not be given a default at all.
 *
 * Two halves, deliberately separate: deciding what changed, and applying the
 * subset the user ticked. They were one block before, which is how the second
 * half ended up keyed on a dimension id while the first produced ids that were
 * not dimension ids.
 */

export interface DefaultValueChange {
  id: string;
  type: 'filter_value' | 'date_range';
  label: string;
  description: string;
}

export interface PendingDimensionFilter {
  dimension_id: string;
  comparator?: string;
  value?: string | string[];
}

const BAR_PREFIX = 'barfilter_';
const PANEL_PREFIX = 'filter_';

/** A filter's id for this dialog. Bar filters are keyed by their OWN id, not
 *  their dimension: one can target the same dimension as a legacy panel, and a
 *  shared key would make a single checkbox stand for both. */
export const barFilterChangeId = (f: Pick<DashboardFilterOption, 'id' | 'dimension_id'>) =>
  `${BAR_PREFIX}${f.id || f.dimension_id}`;

export const panelFilterChangeId = (dimensionId: string) => `${PANEL_PREFIX}${dimensionId}`;

const asList = (v: string | string[] | undefined | null): string[] =>
  v === undefined || v === null ? [] : Array.isArray(v) ? v : [v];

const sameValues = (a: string[], b: string[]) =>
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

const describe = (saved: string[], pending: string[]) =>
  `${saved.length > 0 ? saved.join(', ') : '(none)'} → ${pending.length > 0 ? pending.join(', ') : '(none)'}`;

export interface CollectInput {
  components: DashboardComponent[];
  filters: DashboardFilterOption[];
  pendingDimensionFilters: PendingDimensionFilter[];
  /** Already-resolved labels for the date range row; null when it is unchanged. */
  dateRange?: { savedLabel: string; pendingLabel: string } | null;
}

/**
 * Every saved default this save would change, in the order the dialog lists
 * them: the date range first, then filters.
 */
export function collectDefaultValueChanges(input: CollectInput): DefaultValueChange[] {
  const changes: DefaultValueChange[] = [];

  if (input.dateRange) {
    changes.push({
      id: 'date_range',
      type: 'date_range',
      label: 'Date Range',
      description: `${input.dateRange.savedLabel} → ${input.dateRange.pendingLabel}`,
    });
  }

  const pendingFor = (dimensionId: string) =>
    asList(input.pendingDimensionFilters.find((f) => f.dimension_id === dimensionId)?.value);

  const push = (id: string, dimensionId: string, saved: string | string[] | undefined, label: string) => {
    const savedValues = asList(saved);
    const pendingValues = pendingFor(dimensionId);
    if (sameValues(savedValues, pendingValues)) return;
    changes.push({
      id,
      type: 'filter_value',
      label: `Filter: ${label}`,
      description: describe(savedValues, pendingValues),
    });
  };

  input.components.forEach((component) => {
    const fc = component.filter_config;
    if (component.object_type !== 'FILTER' || !fc?.dimension_id) return;
    push(
      panelFilterChangeId(fc.dimension_id),
      fc.dimension_id,
      fc.default_value ?? undefined,
      fc.input_label || component.title || fc.dimension_id,
    );
  });

  input.filters.forEach((f) => {
    if (!f.dimension_id) return;
    push(
      barFilterChangeId(f),
      f.dimension_id,
      f.default_value ?? undefined,
      f.input_label || f.dimension_id,
    );
  });

  return changes;
}

export interface ApplyInput {
  changes: DefaultValueChange[];
  /** Which rows the user left ticked. Missing counts as ticked, matching the dialog's default. */
  checked: Map<string, boolean>;
  components: DashboardComponent[];
  filters: DashboardFilterOption[];
  pendingDimensionFilters: PendingDimensionFilter[];
}

/**
 * The components and filters to save, with the ticked defaults applied.
 *
 * Copies rather than mutating: the originals are React state, and the caller
 * hands both the new arrays and the same values to the save.
 */
export function applyDefaultValueChanges(input: ApplyInput): {
  components: DashboardComponent[];
  filters: DashboardFilterOption[];
} {
  const components = input.components.map((c) => ({
    ...c,
    filter_config: c.filter_config ? { ...c.filter_config } : undefined,
  }));
  const filters = input.filters.map((f) => ({ ...f }));

  const pendingFor = (dimensionId: string): string[] | undefined => {
    const value = input.pendingDimensionFilters.find((f) => f.dimension_id === dimensionId)?.value;
    if (value === undefined || value === null) return undefined;
    const list = Array.isArray(value) ? [...value] : [value];
    return list.length > 0 ? list : undefined;
  };

  input.changes.forEach((change) => {
    if (change.type !== 'filter_value') return;
    if (input.checked.get(change.id) === false) return;

    if (change.id.startsWith(BAR_PREFIX)) {
      const key = change.id.slice(BAR_PREFIX.length);
      const target = filters.find((f) => (f.id || f.dimension_id) === key);
      if (target?.dimension_id) target.default_value = pendingFor(target.dimension_id);
      return;
    }

    const dimensionId = change.id.slice(PANEL_PREFIX.length);
    const component = components.find(
      (c) => c.object_type === 'FILTER' && c.filter_config?.dimension_id === dimensionId,
    );
    if (component?.filter_config) {
      component.filter_config.default_value = pendingFor(dimensionId);
    }
  });

  return { components, filters };
}
